import { useState } from 'react';
import { Link } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { CASH_MOVEMENT_TYPES, PAYMENT_METHODS, type CashMovementType, type CashSummary, type PaymentMethod } from '@pos/core';
import type { CashSessionRow } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { useOnline } from '../../lib/use-online.ts';
import { Alert, Button, Card, PageHeader, Select, Spinner, cx } from '../../ui/index.tsx';
import { useMoney } from '../products/data.ts';
import { usePosSettings } from '../pos/state.ts';
import { printHtml } from '../pos/ticket.ts';
import { CashMovementDialog, CloseCashDialog, Line, OpenCashDialog } from './CashDialogs.tsx';
import {
  chooseRegister,
  useCashSummary,
  useCurrentRegister,
  useOpenSession,
  useRegisterNames,
  useRegisters,
  useServerCashSummary,
  useSessionData,
  type ServerCashSummary,
} from './data.ts';
import { cashReportHtml } from './report.ts';

const timeFormat = new Intl.DateTimeFormat('es-AR', { dateStyle: 'short', timeStyle: 'short' });

type ShownSummary = Omit<CashSummary, 'expected'> & { expected: number | null };

/**
 * Caja: abrir, ingresos/retiros/gastos, estado y cierre con arqueo. La caja es del negocio (la
 * comparten todos los dispositivos); con conexión el estado suma lo de todos.
 */
export function CashPage() {
  const { business, membership, isOwner } = useSession();
  const money = useMoney();
  const online = useOnline();
  const settings = usePosSettings();
  const registers = useRegisters();
  const register = useCurrentRegister();
  const registerName = useRegisterNames();
  const session = useOpenSession();
  const data = useSessionData(session?.id);
  const local = useCashSummary(session, data);
  const server = useServerCashSummary(session);
  const members = useLiveQuery(() => db.memberships.where('business_id').equals(business.id).toArray(), [business.id]);
  const devices = useLiveQuery(() => db.devices.where('business_id').equals(business.id).toArray(), [business.id]);
  const [opening, setOpening] = useState(false);
  const [moving, setMoving] = useState<CashMovementType | null>(null);
  const [closing, setClosing] = useState(false);
  const [result, setResult] = useState<{ session: CashSessionRow; summary: ShownSummary; counted: number; difference: number } | null>(null);

  const person = (id: string | null | undefined) => members?.find((m) => m.user_id === id)?.display_name ?? '—';
  const deviceName = (id: string | null | undefined) => devices?.find((d) => d.id === id)?.name ?? 'otro dispositivo';
  const blind = settings.blind_close && !isOwner;
  const shared = !!session && !session.device_only;
  // Con conexión, el estado de la caja compartida viene del servidor (todos los dispositivos).
  const summary: ShownSummary | null = shared && server.summary ? server.summary : local;
  const fromServer = shared && !!server.summary;
  const placeName = session?.device_only
    ? `${registerName(session.register_id)} · solo ${deviceName(session.device_id)}`
    : registerName(session?.register_id ?? register?.id);

  const print = (s: CashSessionRow, sum: ShownSummary) => {
    printHtml(
      cashReportHtml(
        { businessName: business.name, placeName, session: s, summary: sum, openedBy: person(s.opened_by), closedBy: s.closed_by ? person(s.closed_by) : null, money },
        settings.ticket_width,
      ),
    );
  };

  if (session === undefined || registers === undefined) {
    return (
      <div className="flex justify-center py-12 text-slate-500">
        <Spinner />
      </div>
    );
  }

  const picker =
    registers.length > 1 && !session?.device_only ? (
      <div className="w-44 sm:w-56">
        <Select value={register?.id ?? ''} onChange={(e) => void chooseRegister(e.target.value)} aria-label="Caja que usa este dispositivo">
          {registers.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </Select>
      </div>
    ) : null;

  const header = (
    <PageHeader
      title={`Caja${placeName ? `: ${placeName}` : ''}`}
      // Con la caja abierta no hace falta: en el celular ocupa lugar y empuja los botones para abajo.
      description={session ? undefined : 'La caja es del negocio: la pueden usar todos los dispositivos a la vez.'}
      actions={
        <>
          {picker}
          {isOwner && (
            <Link to="/caja/historial">
              <Button variant="secondary">Historial de cajas</Button>
            </Link>
          )}
        </>
      }
    />
  );

  if (result) {
    const diff = result.difference;
    return (
      <div className="mx-auto max-w-lg space-y-4">
        {header}
        <Card className="space-y-3 text-center">
          <p className="text-sm text-slate-500">Caja cerrada</p>
          <p className="text-3xl font-semibold tabular-nums">{money(result.counted)}</p>
          <p className={cx('text-lg font-medium', diff === 0 ? 'text-emerald-700' : diff < 0 ? 'text-red-700' : 'text-amber-700')}>
            {diff === 0 ? 'Sin diferencia' : diff < 0 ? `Faltan ${money(-diff)}` : `Sobran ${money(diff)}`}
          </p>
          <p className="text-xs text-slate-500">Si después llega una venta atrasada de algún dispositivo, el cierre se recalcula solo.</p>
          <div className="flex justify-center gap-2">
            <Button variant="secondary" onClick={() => print(result.session, result.summary)}>
              Imprimir cierre
            </Button>
            <Button onClick={() => setResult(null)}>Listo</Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="mx-auto max-w-lg space-y-4">
        {header}
        <Card className="space-y-3 text-center">
          <p className="font-medium">La caja está cerrada.</p>
          <p className="text-sm text-slate-500">
            Abrila con el efectivo que hay para dar vuelto. Una vez abierta la usan todos los dispositivos.
            {settings.require_open_cash ? ' Para vender hace falta la caja abierta.' : ''}
          </p>
          <div>
            <Button size="lg" onClick={() => setOpening(true)}>
              Abrir caja
            </Button>
          </div>
        </Card>
        <OpenCashDialog open={opening} onClose={() => setOpening(false)} />
      </div>
    );
  }

  const movements = fromServer
    ? (server.summary as ServerCashSummary).movements
    : [...(data?.movements ?? [])].sort((a, b) => b.occurred_at.localeCompare(a.occurred_at));
  const showExpected = !blind && summary?.expected != null;

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      {header}
      <Card flush className="space-y-1 px-4 py-3 text-sm">
        <p className="text-slate-600">
          Abierta {timeFormat.format(new Date(session.opened_at))} por {person(session.opened_by)} en {deviceName(session.device_id)} · fondo{' '}
          {money(session.opening_amount)}
        </p>
        {session.device_only && (
          <p className="text-amber-700">Esta caja se abrió sin conexión y es solo de este dispositivo: cerrala desde acá.</p>
        )}
      </Card>
      {shared && !online && (
        <Alert tone="info">Sin conexión: se muestra solo lo que se cargó en este dispositivo. Con conexión se suma lo de todos.</Alert>
      )}
      {shared && online && !server.summary && server.error && <Alert tone="warning">No se pudo traer el estado de la caja: {server.error}</Alert>}

      {!summary ? (
        <div className="flex justify-center py-6 text-slate-500">
          <Spinner />
        </div>
      ) : (
        <>
          {/* Los botones van arriba: en el celular tienen que verse sin deslizar. */}
          <div className="grid grid-cols-6 gap-2 sm:flex sm:flex-wrap">
            {(Object.keys(CASH_MOVEMENT_TYPES) as CashMovementType[]).map((t) => (
              <Button key={t} variant="secondary" className="col-span-2 w-full sm:w-auto" onClick={() => setMoving(t)}>
                {CASH_MOVEMENT_TYPES[t].label}
              </Button>
            ))}
            <Button variant="secondary" className="col-span-3 w-full sm:w-auto" onClick={() => print(session, summary)}>
              Imprimir estado
            </Button>
            <div className="hidden flex-1 sm:block" />
            <Button variant="danger" className="col-span-3 w-full sm:w-auto" onClick={() => setClosing(true)}>
              Cerrar caja
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
            <Stat label="Ventas" value={money(summary.salesTotal)} hint={`${summary.salesCount} ${summary.salesCount === 1 ? 'venta' : 'ventas'}`} />
            <Stat label="Devoluciones" value={money(summary.returnsTotal)} hint={`${summary.returnsCount}`} />
            {showExpected && (
              <Stat
                label="Efectivo en caja (esperado)"
                value={money(summary.expected!)}
                strong
                className="order-first col-span-2 sm:order-none sm:col-span-1"
              />
            )}
          </div>

          {/* En PC, medios de pago y movimientos lado a lado. */}
          <div className={cx('grid gap-4 lg:items-start', movements.length > 0 && 'lg:grid-cols-2')}>
            <Card className="space-y-2 text-sm">
              <h2 className="font-semibold tracking-tight">Por medio de pago</h2>
              {Object.keys(summary.byMethod).length === 0 && <p className="text-slate-500">Todavía no hay ventas en esta caja.</p>}
              {Object.entries(summary.byMethod).map(([m, v]) => (
                <Line key={m} label={PAYMENT_METHODS[m as PaymentMethod]?.label ?? m} value={money(v)} />
              ))}
              {showExpected && (
                <div className="space-y-1 border-t border-slate-100 pt-2">
                  <Line label="Fondo inicial" value={money(summary.opening)} />
                  <Line label="+ Ventas en efectivo" value={money(summary.cashSales)} />
                  {summary.cashRefunds > 0 && <Line label="− Devoluciones en efectivo" value={money(summary.cashRefunds)} />}
                  {summary.accountPayments > 0 && <Line label="+ Cobros de cuenta corriente" value={money(summary.accountPayments)} />}
                  {summary.ins > 0 && <Line label="+ Ingresos" value={money(summary.ins)} />}
                  {summary.outs > 0 && <Line label="− Retiros" value={money(summary.outs)} />}
                  {summary.expenses > 0 && <Line label="− Gastos" value={money(summary.expenses)} />}
                  <Line label="Efectivo esperado" value={money(summary.expected!)} strong />
                </div>
              )}
            </Card>

            {movements.length > 0 && (
              <Card flush className="divide-y divide-slate-100 text-sm">
                <h2 className="px-4 py-3 font-semibold tracking-tight">Movimientos de efectivo</h2>
                {movements.map((m) => (
                  <div key={m.id} className="flex justify-between gap-3 px-4 py-2">
                    <span className="min-w-0">
                      <span className="font-medium">{CASH_MOVEMENT_TYPES[m.type].label}</span>
                      <span className="text-slate-500">
                        {' '}
                        · {m.reason} · {timeFormat.format(new Date(m.occurred_at))}
                        {fromServer && m.created_by ? ` · ${person(m.created_by)}` : ''}
                      </span>
                    </span>
                    <span className={cx('shrink-0 tabular-nums', m.type === 'in' ? 'text-emerald-700' : 'text-red-700')}>
                      {m.type === 'in' ? '+' : '−'}
                      {money(m.amount)}
                    </span>
                  </div>
                ))}
              </Card>
            )}
          </div>
          {blind && <Alert tone="info">Al cerrar vas a contar el efectivo sin ver cuánto debería haber (lo configura el dueño).</Alert>}

          <CashMovementDialog
            session={session}
            type={moving}
            onClose={() => {
              setMoving(null);
              void server.refresh();
            }}
          />
          {local && (
            <CloseCashDialog
              session={session}
              summary={local}
              open={closing}
              blind={blind}
              onClose={() => setClosing(false)}
              onClosed={(counted, difference, expected) => {
                setClosing(false);
                setResult({
                  session: { ...session, closed_at: new Date().toISOString(), closed_by: membership.user_id, counted_cash: counted, expected_cash: expected, difference },
                  summary: { ...summary, expected },
                  counted,
                  difference,
                });
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, hint, strong, className }: { label: string; value: string; hint?: string; strong?: boolean; className?: string }) {
  return (
    <div className={cx('min-w-0 rounded-xl bg-white p-3 shadow-card ring-1 sm:p-4', strong ? 'ring-brand/40' : 'ring-slate-200/80', className)}>
      <p className="text-sm font-medium text-slate-500">{label}</p>
      <p className={cx('truncate text-xl tracking-tight tabular-nums sm:text-2xl', strong ? 'font-bold' : 'font-semibold')}>{value}</p>
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
}
